import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import { useProgress } from '@/hooks/useProgress';
import { useAuth } from '@/context/AuthContext';
import StudentLevelCard from '@/components/feature/StudentLevelCard';
import { MODULES, lessonsOfModule } from '@/data/lessons';
import { isLessonMastered, isLessonUnlocked, isModuleComplete } from '@/lib/progress/gating';

export default function LessonsPage() {
  const { progress, masteredCount, resetAll } = useProgress();
  const { user } = useAuth();
  const reviewAccess = user?.accessLevel === 'review' || user?.accessLevel === 'admin';
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        {/* Cabeçalho */}
        <section className="w-full bg-background-100 px-4 md:px-8 py-12 md:py-16">
          <div className="mx-auto max-w-6xl">
            <span className="font-label text-xs font-bold uppercase tracking-wide text-primary-700">
              Trilha de prática
            </span>
            <h1 className="mt-3 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
              Leitura musical por domínio
            </h1>
            <p className="mt-4 max-w-2xl text-foreground-700">
              {reviewAccess
                ? 'Acesso completo de revisão ativado nesta conta: você pode visitar as 48 aulas sem precisar concluir as anteriores. O histórico, os acertos e o ranking continuam baseados apenas na prática real.'
                : 'A trilha começa pela clave de sol e só avança depois do domínio real de cada região: central, aguda, grave e leitura completa. Depois entram os símbolos e os conteúdos seguintes. A próxima etapa só é liberada quando a anterior estiver dominada.'}
            </p>

            <div className="mt-6">
              <StudentLevelCard progress={progress} />
            </div>

            {user?.accessLevel === 'admin' && (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-orange-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-2xl text-orange-700">
                    <i className="ri-shield-user-line" />
                  </span>
                  <div>
                    <p className="font-heading text-sm font-extrabold text-slate-900">Minha conta · Administração</p>
                    <p className="mt-1 text-xs text-slate-600">Veja novos cadastros, resultados e a evolução de cada aluno.</p>
                  </div>
                </div>
                <Link
                  to="/admin"
                  className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-5 py-3 text-sm font-extrabold text-white hover:bg-orange-600"
                >
                  Abrir painel administrativo <i className="ri-arrow-right-line" />
                </Link>
              </div>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <Badge tone="accent" icon="ri-medal-line">
                {reviewAccess ? '48 de 48 aulas liberadas para revisão' : String(masteredCount) + ' de 48 aulas dominadas'}
              </Badge>
              {confirming ? (
                <div className="flex flex-wrap items-center gap-3 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-2">
                  <span className="text-sm text-secondary-900">
                    Apagar todo o progresso desta conta?
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      resetAll();
                      setConfirming(false);
                    }}
                  >
                    Sim, apagar
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                    Cancelar
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  icon="ri-delete-bin-line"
                  onClick={() => setConfirming(true)}
                >
                  Reiniciar meu progresso
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* Módulos */}
        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="mx-auto flex max-w-6xl flex-col gap-12">
            {MODULES.map((module) => {
              const lessons = lessonsOfModule(module.id);
              const complete = isModuleComplete(progress, module.id);
              const doneInModule = lessons.filter((lesson) => isLessonMastered(progress, lesson.id)).length;
              return (
                <div key={module.id}>
                  <div className="flex flex-col gap-3 border-b border-background-200 pb-4 md:flex-row md:items-end md:justify-between">
                    <div className="flex items-start gap-4">
                      <span className="w-12 h-12 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 font-heading text-lg font-bold text-primary-700">
                        {module.id}
                      </span>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                            {module.title}
                          </h2>
                          {complete ? (
                            <Badge tone="accent" icon="ri-check-double-line">
                              Módulo concluído
                            </Badge>
                          ) : null}
                        </div>
                        <p className="mt-1 text-sm text-foreground-700">{module.summary}</p>
                      </div>
                    </div>
                    <span className="text-xs font-label font-semibold text-foreground-500">
                      {doneInModule} de {lessons.length} dominadas
                    </span>
                  </div>

                  <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {lessons.map((lesson) => {
                      const mastered = isLessonMastered(progress, lesson.id);
                      const unlocked = reviewAccess || isLessonUnlocked(progress, lesson.id);

                      const inner = (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="font-heading text-sm font-bold text-foreground-500">
                              Aula {lesson.id}
                            </span>
                            {mastered ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] font-label font-semibold text-accent-800">
                                <i className="ri-medal-line w-3.5 h-3.5 flex items-center justify-center" />
                                Dominada
                              </span>
                            ) : unlocked ? (
                              <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-[11px] font-label font-semibold text-primary-800">
                                {reviewAccess ? 'Liberada para revisão' : 'Disponível'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-background-200 px-2.5 py-0.5 text-[11px] font-label font-semibold text-foreground-600">
                                <i className="ri-lock-2-line w-3.5 h-3.5 flex items-center justify-center" />
                                Bloqueada
                              </span>
                            )}
                          </div>
                          <h3
                            className={`mt-3 font-heading text-base font-bold ${
                              unlocked ? 'text-foreground-950' : 'text-foreground-600'
                            }`}
                          >
                            {lesson.title}
                          </h3>
                          <p className="mt-2 flex-1 text-sm text-foreground-600">{lesson.objective}</p>
                          {unlocked ? (
                            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-label font-semibold text-primary-700">
                              {mastered ? 'Refazer aula' : 'Praticar aula'}
                              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                            </span>
                          ) : (
                            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-label font-semibold text-foreground-500">
                              <i className="ri-lock-2-line w-4 h-4 flex items-center justify-center" />
                              Domine a aula {lesson.id - 1} para liberar
                            </span>
                          )}
                        </>
                      );

                      if (!unlocked) {
                        return (
                          <div
                            key={lesson.id}
                            className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5 opacity-80"
                          >
                            {inner}
                          </div>
                        );
                      }

                      return (
                        <Link
                          key={lesson.id}
                          to={`/aulas/${lesson.id}`}
                          className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors cursor-pointer hover:border-primary-300 hover:bg-primary-50"
                        >
                          {inner}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Aviso de fase */}
        <section className="w-full px-4 md:px-8 pb-16">
          <div className="mx-auto max-w-6xl rounded-lg border border-background-200 bg-secondary-50 p-5 md:p-6">
            <div className="flex items-start gap-3">
              <i className="ri-information-line w-5 h-5 mt-0.5 flex items-center justify-center text-secondary-700" />
              <p className="text-sm text-foreground-800">
                As etapas são uma ferramenta de apoio para iniciantes. O sistema gera variações,
                reforça o que você erra e aumenta a exigência aos poucos: os treinos iniciais começam
                com metas mais leves e as fases misturadas e de velocidade chegam a 95–98% de domínio.
                O objetivo é praticar leitura musical até ela ficar natural — sem substituir o professor.
                Seu progresso, XP e histórico ficam vinculados à sua conta.
              </p>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}